<script setup>
import { useTheme } from '../composables/useTheme';
import HrListSelect from './HrListSelect.vue';

/**
 * Setting select — a full-width row pairing a named setting with the value chosen for it.
 *
 * The select counterpart to HrSettingSwitch, and it exists for the width. A list item's trailing
 * section is sized to its content, so a select placed there collapses to an empty stub until
 * something is picked — narrowest at the moment the user most needs to see it is a control. This row
 * gives the label the remaining space and the control a fixed share of it.
 *
 * Reach for it over a bordered HrList of items when the rows are a set of decisions rather than a
 * collection being browsed: Material 3 puts an outlined container around content a reader scans,
 * not around a form.
 *
 * The row carries no hint or error surface, so the control suppresses the space a field normally
 * reserves for one. Left in, it adds a blank strip under every row that reads as the gap between
 * them, and a column of rows drifts twice as far apart as it was spaced to be.
 *
 * @status recommended
 * @group forms-input
 * @useWhen A named setting whose value is chosen from a list
 * @avoidWhen The setting is on or off — use HrSettingSwitch
 * @avoidWhen The rows are data being browsed rather than settings — use HrList with HrListItem
 * @related HrSettingSwitch
 * @related HrListSelect
 * @a11y The row's `label` is adjacent text rather than a programmatic label, so the control has no
 *   accessible name of its own. Quasar offers no way to give it one without a visible label: a
 *   passed `aria-label` lands on `.q-field__native`, a div, while the combobox is an input inside
 *   it. Worth revisiting if HrSelect ever forwards to the focus target.
 */
defineOptions({ name: 'HrSettingSelect' });

/** @typedef {import('./HrListSelect.vue').HrListSelectEntry} HrListSelectEntry */

const props = defineProps({
  /**
   * The setting's name, shown on the left and given the remaining width.
   * @example Materials, Type, and Array
   */
  label: {
    type: String,
    required: true,
  },
  /**
   * Supporting text below the label: what the setting means, or where its value came from, in one
   * sentence the reader needs to decide. A longer or occasional explanation goes behind an
   * HrIconHintBtn, never in a tooltip.
   */
  description: {
    type: String,
    default: null,
  },
  /** Currently selected value. Supports v-model. */
  modelValue: {
    type: null,
    default: null,
  },
  /**
   * Flat or grouped option list, in the shape HrListSelect takes.
   * @type {HrListSelectEntry[]}
   * @example [{"label":"Program name","caption":"Program"},{"label":"Target label","caption":"Target"}]
   */
  options: {
    type: Array,
    default: () => [],
  },
  /** Renders each option's caption above its label. */
  captionAbove: {
    type: Boolean,
    default: false,
  },
  /** Shows a clear button once a value is selected. */
  clearable: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims the whole row. */
  disable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Emitted when the selected value changes; null when the field is cleared. */
  'update:modelValue': null,
});

const { hasTheme } = useTheme();
</script>

<template>
  <div
    class="hr-setting-select"
    :class="{ 'hr-setting-select--themed': hasTheme, 'hr-setting-select--disabled': props.disable }"
  >
    <div class="hr-setting-select__text">
      <span
        class="hr-setting-select__label"
        :class="hasTheme ? 'hr-body-large' : undefined"
      >{{ props.label }}</span>
      <span
        v-if="props.description"
        class="hr-setting-select__description"
        :class="hasTheme ? 'hr-body-medium' : undefined"
      >{{ props.description }}</span>
    </div>
    <hr-list-select
      class="hr-setting-select__control"
      :model-value="props.modelValue"
      :options="props.options"
      :caption-above="props.captionAbove"
      :clearable="props.clearable"
      :disable="props.disable"
      hide-bottom-space
      @update:model-value="emit('update:modelValue', $event)"
    />
  </div>
</template>

<style lang="scss" scoped>
.hr-setting-select {
  display: flex;
  align-items: center;
  gap: var(--app-comp-setting-select-gap);
  width: 100%;
}

.hr-setting-select__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.hr-setting-select__control {
  flex: 0 0 var(--app-comp-setting-select-control-width);
  max-width: 100%;
}

.hr-setting-select--themed .hr-setting-select__label {
  color: var(--app-on-surface);
}

.hr-setting-select--themed .hr-setting-select__description {
  color: var(--app-on-surface-variant);
}

.hr-setting-select--themed.hr-setting-select--disabled .hr-setting-select__label,
.hr-setting-select--themed.hr-setting-select--disabled .hr-setting-select__description {
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

// The row stacks before the control gets too narrow to read a field name in.
@media (max-width: 599px) {
  .hr-setting-select {
    align-items: stretch;
    flex-direction: column;
    gap: 4px;
  }

  .hr-setting-select__control {
    flex: 1 1 auto;
  }
}
</style>
